import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  combineLatest,
  map,
  merge,
  BehaviorSubject,
  Observable,
  Subject,
  takeUntil,
  from,
  Subscription,
  fromEvent,
} from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import * as cornerstone from 'cornerstone-core';

import { StateManagerProjectService } from '@state-manager';
import { TriggerAction } from '@shared/actions';
import { ToolbarService } from '@shared/toolbar/toolbar.service';
import { CornerstoneService } from './cornerstone.service';
import { OCCViewerService } from './occ-viewer/occ-viewer.service';
import { ContextMenuService } from '../context-menu/context-menu.service';
import {
  BoneModelType,
  CornerstoneAdditionalTool,
  CornerstoneMouseButton,
  CornerstoneTool,
  HotKey,
  HotKeySettings,
  IViewerViewOrder,
  ViewerComponentKeyActionName,
  ViewerComponentKeyActionNameList,
  ViewerView,
  ViewWindowIndex,
} from './data';
import { IViewersObjectBoolean } from './cornerstone.data';
import { KeyInteractionService } from '../interaction';
import {
  DataRequestService,
  ProjectActionsRequestService,
  TimeTrackerService,
} from './services';
import { ViewerService } from './viewer.service';
import Utils from '@shared/utils';
import { constantStrings } from '@shared/helpers/constants';
import { IClaimantDetailsDto } from './services/project-actions.data';
import { AuthService } from '@shared/services/auth.service';
import { ConfirmDialogService } from '@shared/confirm-dialog';
import { TextsForErrorsDialogs } from '@shared/confirm-dialog/confirm-dialog.data';
import { SharedStateService } from '@shared/services/shared-state.service';
import * as MMB from './pan-tool-mmb-handler'; // MMB(mouse middle button)

@Component({
  selector: 'app-viewer',
  templateUrl: 'viewer.component.html',
  styleUrls: ['viewer.component.scss'],
})
export class ViewerComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() useHotKeys = true;

  readonly ViewWindowIndex = ViewWindowIndex;
  @ViewChild('centerOfToolbarContent', { static: true })
  centerOfToolbarContent!: TemplateRef<any>;

  @ViewChildren(
    'firstViewWindow, secondViewWindow, thirdViewWindow, forthViewWindow',
    { read: ElementRef }
  )
  viewsRefs!: QueryList<ElementRef<HTMLElement>>;

  public fourViewsAction: TriggerAction;
  public singleViewAction: TriggerAction;
  public twoViewsAction: TriggerAction;
  private _updatedMaskWillNotLoaded = false;
  private _isProcessingErrorOpened = false;
  hostElement = this.elementRef.nativeElement; // Access the host element
  isPanToolSelectedByMmb: boolean = false;
  is3dPanToolSelectedByMmb: boolean = false;

  private _catchForProcessing: () => void;

  public viewerViews: IViewerViewOrder[] = Object.values(ViewerView).map(
    (x, i) => ({
      name: x,
      order: i,
      id: i,
    })
  );

  private previousViewMode: number = 4;

  public viewNames = Object.values(ViewerView);
  public order = this.viewerViews.map((x) => x.order);

  private _oldViewerViews: IViewerViewOrder[] = [...this.viewerViews];
  private _basedViewerViews: IViewerViewOrder[] = Object.values(ViewerView).map(
    (x, i) => ({
      name: x,
      order: i,
      id: i,
    })
  );
  private _oldFourViewsOrder: IViewerViewOrder[] = [...this.viewerViews];
  private _oldViewports: cornerstone.CornerstoneViewport[] = [];

  private _isImageProcessed$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  private _originalMaskBlob$: BehaviorSubject<Blob | undefined> =
    new BehaviorSubject<Blob | undefined>(undefined);
  private _updatedMaskBlob$: BehaviorSubject<Blob | null | undefined> =
    new BehaviorSubject<Blob | null | undefined>(undefined);

  private _unsubscribe$ = new Subject();
  private _subscriptionToRefreshToken: Subscription | undefined;

  @HostListener('window:keydown', ['$event'])
  checkOfKeyDown(event): void {
    if (event.ctrlKey) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  @HostListener('window:click')
  checkOfClick(): void {
    this._fireUpdateOfToken();
  }

  @HostListener('window:keyup', ['$event'])
  keyupHostHandler(event: KeyboardEvent) {
    /* close context menu via keyboard interaction when it was in open state */
     if (this._contextMenuService.contextMenuFlag && (event.code === HotKey.One || event.code === HotKey.Two || event.code === HotKey.Four ||
      event.code === HotKey.Numpad1 || event.code === HotKey.Numpad2 || event.code === HotKey.Numpad4)) {
      this._contextMenuService.closeMenu();
      this._contextMenuService.contextMenuFlag = false;
     }
     //closes brush, eraser and lasso menu on view change
     if(event.code === HotKey.One || event.code === HotKey.Two || event.code === HotKey.Four ||
      event.code === HotKey.Numpad1 || event.code === HotKey.Numpad2 || event.code === HotKey.Numpad4) {
        this.cornerStoneService._closeOpenedMenu();
      }
  };

  constructor(
    public stateManagerProjectService: StateManagerProjectService,
    public cornerStoneService: CornerstoneService,
    public occViewerService: OCCViewerService,
    private _toolbarService: ToolbarService,
    private _contextMenuService: ContextMenuService,
    private _viewContainerRef: ViewContainerRef,
    private _cdr: ChangeDetectorRef,
    private _keyInteractionService: KeyInteractionService,
    private _dataRequestService: DataRequestService,
    private _viewerService: ViewerService,
    private _projectActionsRequestService: ProjectActionsRequestService,
    private _timeTrackerService: TimeTrackerService,
    private _authService: AuthService,
    private _route: Router,
    private _confirmDialog: ConfirmDialogService,
    private sharedState: SharedStateService,
    private elementRef: ElementRef
  ) {
    this._catchForProcessing = () => {
      if (!this._isProcessingErrorOpened) {
        this._isProcessingErrorOpened = true;
        this._confirmDialog
          .openConfirmDialog(
            TextsForErrorsDialogs.PROCESSING_ERROR_INVALID_FILE_TYPE,
            TextsForErrorsDialogs.CLOSE
          )
          .subscribe((_) => {
            this._isProcessingErrorOpened = false;
          });
      }
    };
    this.viewerViews[0].id = 1;
    this.viewerViews[1].id = 4;
    this.viewerViews[2].id = 2;
    this.viewerViews[3].id = 3;

    this.fourViewsAction = new TriggerAction('four-views', '', () => {
      for (let i = 0; i < 4; i++) {
        this.viewerViews[i].order = this._oldFourViewsOrder.find(
          (x) => x.name === this.viewerViews[i].name
        )!.order;
      }
      this.order = this.viewerViews.map((x) => x.order);
      this.viewerViews.sort((a, b) => a.order - b.order);
      this.previousViewMode = this.stateManagerProjectService.viewMode;
      this.stateManagerProjectService.viewMode = 4;
    });

    this.singleViewAction = new TriggerAction('single-view', '', () => {
      this.previousViewMode = this.stateManagerProjectService.viewMode;
      this.stateManagerProjectService.viewMode = 1;
      if (this.previousViewMode === 4) {
        this._oldFourViewsOrder = Utils.deepClone(this.viewerViews);
      }
      if (this._checkFirstWindowNotSelected()) {
        this._swapWindowViews(
          this.stateManagerProjectService.currentSelectedWindow,
          this.viewerViews[0]
        );
        this.order = this.viewerViews.map((x) => x.order);
        this.viewerViews.sort((a, b) => a.order - b.order);
      }
    });

    this.twoViewsAction = new TriggerAction('two-views', '', () => {
      this.previousViewMode = this.stateManagerProjectService.viewMode;
      this.stateManagerProjectService.viewMode = 2;
      if (this.previousViewMode === 4) {
        this._oldFourViewsOrder = Utils.deepClone(this.viewerViews);
      }
      let isSortNeeded = false;
      if (this._checkFirstWindowNotSelected()) {
        this._swapWindowViews(
          this.stateManagerProjectService.currentSelectedWindow,
          this.viewerViews[0]
        );
        isSortNeeded = true;
      }
      if (
        ![
          this.stateManagerProjectService.currentSelectedWindow,
          this.viewerViews[1].id,
        ].includes(ViewWindowIndex.Model3D)
      ) {
        this._swapWindowViews(
          this.ViewWindowIndex.Model3D,
          this.viewerViews.find((x) => x.order === 1)
        );
        isSortNeeded = true;
      }
      if (isSortNeeded) {
        this.order = this.viewerViews.map((x) => x.order);
        this.viewerViews.sort((a, b) => a.order - b.order);
      }
    });

    this._basedViewerViews = this.viewerViews.map((el) => {
      return {
        name: el.name,
        order: el.order,
        id: el.id,
      };
    });

    this.cornerStoneService.resetViewsEmitter
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((value) => {
        if (value) {
          this.order = this.viewerViews.map((x) => {
            const foundedElement = this._basedViewerViews.find(
              (el) => el.id === x.id
            );
            return foundedElement!.order;
          });
          this.viewerViews = JSON.parse(JSON.stringify(this._basedViewerViews));
        }
      });

    this.stateManagerProjectService.isProcessOfShowDiffHide$
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(() => this._cdr.markForCheck());

      this._subscriptionForDataProcessing();

    this.sharedState.timeOutsubject.subscribe(res => {
      if((this.stateManagerProjectService.isEditAllowed && !this.stateManagerProjectService.isApprovedClicked && !this.stateManagerProjectService.isRejectClicked) && (!this.stateManagerProjectService.isCaseApproved || !this.stateManagerProjectService.isCaseRejected)) {
        this.checkInactivity(res);
      } else {
        this.sharedState.timeOutCallbacsubject.next(res);
      }
    })
  }

  public checkInactivity(res) {
    this.cornerStoneService.saveMasks(() => {
      this.sharedState.timeOutCallbacsubject.next(res);
    });
  }
  
  selectWindow(
    index: ViewWindowIndex,
    event?: PointerEvent | MouseEvent,
    viewWindow: HTMLElement | null = null
  ): void {
    this._contextMenuService.closeMenu();

    if (this.occViewerService.isViewerLoading) return;

    if (this.stateManagerProjectService.currentSelectedWindow !== index) {
      this.stateManagerProjectService.currentSelectedWindow = index;
      if (viewWindow) {
        this.stateManagerProjectService.currentSelectedWindowElement =
          viewWindow;
      } else {
        this.stateManagerProjectService.currentSelectedWindowElement =
          this._findViewWindow(index);
      }
    }
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this._fireUpdateOfToken();
  }

  ngOnInit(): void {
    this.stateManagerProjectService.isAlreadyClosedApp = false;
    this.stateManagerProjectService.isApprovedClicked = false;
    this.stateManagerProjectService.isRejectClicked = false;
    this._projectActionsRequestService
      .requestGetEditPermission(this.stateManagerProjectService.caseId)
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(
        (res) => {
          // change here for enable changes
          this.stateManagerProjectService.isDisabledAllToolsForChangeMask =
            !res.isEditAllowed;
          this.stateManagerProjectService.isEditAllowed = res.isEditAllowed;
          if (res.isEditAllowed) {
            console.log('Viewer component: Review Timer started');
            this._timeTrackerService.init();
          }
        },
        (err) => {
          this.stateManagerProjectService.isEditAllowed = false;
          this.stateManagerProjectService.isDisabledAllToolsForChangeMask =
            true;
        }
      );
    this._projectActionsRequestService
      .requestGetClaimantDetailsForCase(this.stateManagerProjectService.caseId)
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((res: IClaimantDetailsDto) => {
        this._setClaimantUser(res);
      });

    this._keyInteractionService.init();
    this._contextMenuService.init(this._viewContainerRef);

    if (this.useHotKeys) {
      this._initializeHotKeys();
    }

    this.stateManagerProjectService.currentTool$
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((currentTool) => {
        let exitEditImageToolsActiveState = false;
        let exitViewToolsActiveState = false;

        if (currentTool !== CornerstoneTool.EmptyTool) {
          exitEditImageToolsActiveState = true;
        }

        if (
          currentTool &&
          [CornerstoneTool.Pan, CornerstoneTool.Zoom].includes(currentTool)
        ) {
          exitViewToolsActiveState = true;
        }

        this._keyInteractionService.setActivateState<ViewerComponentKeyActionName>(
          'ExitEditImageTools',
          exitEditImageToolsActiveState
        );
        this._keyInteractionService.setActivateState<ViewerComponentKeyActionName>(
          'ExitViewTools',
          exitViewToolsActiveState
        );
      });

    combineLatest([
      this.stateManagerProjectService.currentSelectedWindow$,
      this.stateManagerProjectService.currentTool$,
    ])
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(([selectedWindow, currentTool]) => {
        if (
          !selectedWindow ||
          (currentTool &&
            [CornerstoneTool.Pan, CornerstoneTool.Zoom].includes(currentTool))
        ) {
          this.cornerStoneService.toolDeactivate(
            CornerstoneAdditionalTool.Pan,
            CornerstoneMouseButton.Middle
          );
          return;
        }

        this.cornerStoneService.additionalToolActivate(
          CornerstoneAdditionalTool.Pan,
          CornerstoneMouseButton.Middle
        );

        this.cornerStoneService.activeMouseWheelTool();
      });

    this._subscriptionForMouseDrag();

    this.stateManagerProjectService.isViewChangeClickedReload$
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((value) => {
        if(value) { 
          this._getData();
        }
        else if(value !== null && this.stateManagerProjectService.isBoneViewLoaded) {
          this.cornerStoneService.setUndoRedoBeforeViewChange();
        }
      });

      this.handlePanToolByMmb();
  }

  ngAfterViewInit(): void {
    this.cornerStoneService.init();
    this.cornerStoneService.initByLarvitar();
    this._toolbarService.setCenterPortal(
      new TemplatePortal(this.centerOfToolbarContent, undefined!, {})
    );

    this._getData();
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next(undefined);
    this._unsubscribe$.complete();
    this.cornerStoneService.destroy();
    this._contextMenuService.destroy();
    if (this.useHotKeys) {
      this._keyInteractionService.removeKeySettingsList([
        ...ViewerComponentKeyActionNameList,
      ]);
    }
    this._keyInteractionService.destroy();
    this.stateManagerProjectService.init();
    MMB.cleanupSubscriptions();
  }

  onViewChanged(): void {
    if (!this.stateManagerProjectService.isViewerLoaded) {
      return;
    }

    for (let i = 1; i < 4; i++) {
      const viewerElement = document.getElementById(`viewer${i}`);
      this._oldViewports[i] = Utils.deepClone(
        cornerstone.getViewport(viewerElement)
      );
    }
    setTimeout(() => {
      this.cornerStoneService.resize();
      const elem = document.getElementById('Hip3dCanvas');
      elem &&
        this.occViewerService.updateCanvasSize(
          this.occViewerService.occViewer!,
          elem
        );
      for (let i = 1; i < 4; i++) {
        const viewerElement = document.getElementById(`viewer${i}`);
        cornerstone.setViewport(viewerElement, this._oldViewports[i]);
      }
    }, 20);
  }

  getSlicesInfo(counter: number): string {
    return (
      String(
        this.stateManagerProjectService.currentActiveSliceIndex[
          `viewer${counter}`
        ] + 1
      ) +
      '/' +
      String(
        this.stateManagerProjectService.totalNumberOfSlices[
          `viewer${counter}`
        ] + 1
      )
    );
  }

  onViewSelected(
    newView: ViewerView,
    oldView: ViewerView,
    select: HTMLSelectElement
  ): void {
    select.value = oldView;
    const foundNewView = this.viewerViews.find((x) => x.name === newView)!;
    const foundOldView = this.viewerViews.find((x) => x.name === oldView)!;
    const oldOrder = foundOldView.order;
    foundOldView.order = foundNewView.order;
    foundNewView.order = oldOrder;
    this.order = this.viewerViews.map((x) => x.order);
    this.viewerViews.sort((a, b) => a.order - b.order);
    this.selectWindow(foundNewView.id);
  }

  openFullsizeMode(index: number, event: any): void {
    if (this.stateManagerProjectService.viewMode === 1) {
      this.stateManagerProjectService.viewMode = this.previousViewMode;
      this.previousViewMode = 1;
      this.order = this.viewerViews.map((x) => {
        const foundedElement = this._oldViewerViews.find(
          (el) => el.id === x.id
        );
        return foundedElement!.order;
      });
      this.viewerViews = this._oldViewerViews;
      event.preventDefault();
      event.stopPropagation();
    } else {
      this._oldViewerViews = this.viewerViews;

      this.previousViewMode = this.stateManagerProjectService.viewMode;
      this.stateManagerProjectService.viewMode = 1;

      this.viewerViews = this.viewerViews.map((x, i) => ({
        ...x,
        order: index === x.id ? 0 : i + 1,
      }));
      this.order = this.viewerViews.map((x) => x.order);
      this.viewerViews.sort((a, b) => a.order - b.order);
    }
  }

  isProcessOfResetChanges(): Observable<boolean> {
    return this.stateManagerProjectService.isProcessOfResetDiff$.pipe(
      map((value: IViewersObjectBoolean) => {
        return (
          value[`viewer${ViewWindowIndex.Coronal}`] ||
          value[`viewer${ViewWindowIndex.Axial}`] ||
          value[`viewer${ViewWindowIndex.Sagittal}`]
        );
      })
    );
  }

  isProcessOfShowDiffHide(value: number): boolean {
    return this.stateManagerProjectService.isProcessOfShowDiffHide[
      `viewer${value}`
    ];
  }

  activatePanTool(): void {
    this.cornerStoneService.toolActivate(CornerstoneTool.Pan);
  }

  activateRotateTool(): void {
    this.cornerStoneService.toolActivate(CornerstoneTool.Rotate);
  }

  activateZoomTool(): void {
    this.cornerStoneService.toolActivate(CornerstoneTool.Zoom);
  }

  openContextMenu(event: MouseEvent, viewWindow: HTMLElement): void {
    event.preventDefault();
    if (
      this.stateManagerProjectService.currentSelectedWindowElement !==
        viewWindow ||
      this.stateManagerProjectService.currentSelectedWindow ===
        ViewWindowIndex.Model3D
    ) {
      return;
    }

    this._contextMenuService.openMenu(event);
  }

  public is3dMoveEnabled(): boolean {
    return (
      this.stateManagerProjectService.current3dTool === 'pan' &&
      !this.stateManagerProjectService.is3dUpdateNeeded
    );
  }

  public is3dRotateEnabled(): boolean {
    return (
      this.stateManagerProjectService.current3dTool === 'rotation' &&
      !this.stateManagerProjectService.is3dUpdateNeeded
    );
  }

  public is3dZoomEnabled(): boolean {
    return (
      this.stateManagerProjectService.current3dTool === 'zoom' &&
      !this.stateManagerProjectService.is3dUpdateNeeded
    );
  }

  private _getData(): void {
    
    const caseId = this.stateManagerProjectService.caseId;

    if(!this.stateManagerProjectService.isViewChangeClickedReload) {
    this._dataRequestService
      .getNrrdImage(caseId)
      .pipe(takeUntil(this._viewerService.preventAllSubscriptions$))
      .subscribe(async (imageBlob) => {
        
        await this.cornerStoneService
          .processNrrdImage(imageBlob)
          .catch((_) => {
            
            if (this.stateManagerProjectService.isViewerLoaded) {
              this._catchForProcessing()
            }
          });
        this._isImageProcessed$.next(true);
        this.sharedState._isImageProcessedNew$.next(true);
      });

      this._dataRequestService
      .getNrrdOriginalMask(caseId)
      .pipe(takeUntil(this._viewerService.preventAllSubscriptions$))
      .subscribe((originalMaskBlob) => {
        
        this._originalMaskBlob$.next(originalMaskBlob);
        this.sharedState._originalMaskBlobNew$.next(originalMaskBlob);
        if (
          this._updatedMaskBlob$.getValue() === undefined &&
          this._updatedMaskWillNotLoaded
        ) {
          this._updatedMaskBlob$.next(originalMaskBlob);
          this.sharedState._updatedMaskBlobNew$.next(originalMaskBlob);
        }
      });
    }
      
    const cb = (isFileAvailable: boolean) => {
      this.stateManagerProjectService.boneModelType = isFileAvailable
        ? BoneModelType.Draft
        : BoneModelType.Raw;
    };
    
    this._dataRequestService
      .getNrrdUpdatedMask(caseId, cb)
      .pipe(takeUntil(this._viewerService.preventAllSubscriptions$))
      .subscribe({
        next: (updatedMaskBlob) => {
          
          updatedMaskBlob &&
            (this.stateManagerProjectService.isMaskWasChanged = true);

          this._updatedMaskBlob$.next(updatedMaskBlob);
        },
        error: () => {
          
          if (this._originalMaskBlob$.getValue() !== undefined) {
            this._updatedMaskBlob$.next(this._originalMaskBlob$.getValue());
          }

          this._updatedMaskWillNotLoaded = true;
        },
      });
      
  }

  private _checkFirstWindowNotSelected(): boolean {
    return ![ViewWindowIndex.Empty, this.viewerViews[0].id].includes(
      this.stateManagerProjectService.currentSelectedWindow
    );
  }

  private _swapWindowViews(
    newViewId: ViewWindowIndex,
    oldView: IViewerViewOrder | undefined
  ): void {
    const newView = this.viewerViews.find((x) => x.id === newViewId);
    if (!newView || !oldView) {
      return;
    }
    [oldView.order, newView.order] = [newView.order, oldView.order];
  }

  private _getNextViewData(): IViewerViewOrder & { viewElement: HTMLElement } {
    const currentViewData = this.viewerViews.find(
      (view) =>
        view.id === this.stateManagerProjectService.currentSelectedWindow
    );
    const availableViews = this.viewsRefs.filter(
      (view) => !view.nativeElement.hidden
    );
    let nextViewOrder = 0;

    if (currentViewData && currentViewData.order + 1 < availableViews.length) {
      nextViewOrder = currentViewData.order + 1;
    }

    const nextViewData = this.viewerViews.find(
      (view) => view.order === nextViewOrder
    )!;

    return {
      ...nextViewData,
      viewElement: availableViews.find(
        (viewRef) =>
          Number(viewRef.nativeElement.dataset.index) === nextViewOrder
      )!.nativeElement,
    };
  }

  private _initializeHotKeys(): void {
    this._keyInteractionService
      .initializeHotKey(
        HotKey.Escape,
        new HotKeySettings<ViewerComponentKeyActionName>({
          keyActionName: 'ExitEditImageTools',
          priority: 1,
          isActive: false,
        })
      )
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(() => {
        if (
          this.stateManagerProjectService.currentTool !==
          CornerstoneTool.EmptyTool
        ) {
          this.cornerStoneService.toolActivate(CornerstoneTool.EmptyTool);
        }
      });

      combineLatest([
        merge(
          this._keyInteractionService
          .initializeHotKey(
            HotKey.Numpad1,
            new HotKeySettings<ViewerComponentKeyActionName>({
              keyActionName: 'NumOneViewActivate',
            })
          ),
          this._keyInteractionService
          .initializeHotKey(
            HotKey.One,
            new HotKeySettings<ViewerComponentKeyActionName>({
              keyActionName: 'OneViewActivate',
            })
          )
        )
      ])
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(() => {
        if (this.stateManagerProjectService.viewMode === 1) {
          return;
        }
        this.singleViewAction.trigger();
      });
      combineLatest([
        merge(
          this._keyInteractionService
          .initializeHotKey(
            HotKey.Numpad2,
            new HotKeySettings<ViewerComponentKeyActionName>({
              keyActionName: 'NumTwoViewsActivate',
            })
          ),
          this._keyInteractionService
          .initializeHotKey(
            HotKey.Two,
            new HotKeySettings<ViewerComponentKeyActionName>({
              keyActionName: 'TwoViewsActivate',
            })
          )
        )
      ])
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(() => {
        if (this.stateManagerProjectService.viewMode === 2) {
          return;
        }
        this.twoViewsAction.trigger();
      });
      combineLatest([
        merge(
          this._keyInteractionService
          .initializeHotKey(
            HotKey.Numpad4,
            new HotKeySettings<ViewerComponentKeyActionName>({
              keyActionName: 'NumFourViewActivate',
            })
          ),
          this._keyInteractionService
          .initializeHotKey(
            HotKey.Four,
            new HotKeySettings<ViewerComponentKeyActionName>({
              keyActionName: 'FourViewActivate',
            })
          )          
        )
      ])
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(() => {
        if (this.stateManagerProjectService.viewMode === 4) {
          return;
        }
        this.fourViewsAction.trigger();
      });

    this._keyInteractionService
      .initializeHotKey(
        HotKey.Space,
        new HotKeySettings<ViewerComponentKeyActionName>({
          keyActionName: 'SelectNextView',
        })
      )
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(() => {
        const viewData = this._getNextViewData();
        this.selectWindow(viewData.id, undefined, viewData.viewElement);
      });

    this._keyInteractionService
      .initializeHotKey(
        HotKey.Escape,
        new HotKeySettings<ViewerComponentKeyActionName>({
          keyActionName: 'ExitViewTools',
          priority: 2,
        })
      )
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(() => {
        this.cornerStoneService.toolActivate(
          this.stateManagerProjectService.currentTool!
        );
      });

    combineLatest([
      merge(
        this._keyInteractionService.initializeHotKey(
          HotKey.CtrlLeft,
          new HotKeySettings<ViewerComponentKeyActionName>({
            keyActionName: 'ZoomViewActivate',
          })
        ),
        this._keyInteractionService.initializeHotKey(
          HotKey.CtrlLeft,
          new HotKeySettings<ViewerComponentKeyActionName>({
            keyActionName: 'ZoomViewDeactivate',
            keyEventType: 'keyup',
          })
        )
      ),
      this.stateManagerProjectService.currentSelectedWindow$,
      this.stateManagerProjectService.currentTool$,
    ])
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(([keyEventMessage, selectedWindow, currentTool]) => {
        if (
          keyEventMessage.eventType === 'keyup' ||
          [ViewWindowIndex.Empty, ViewWindowIndex.Model3D].includes(
            selectedWindow
          ) ||
          (currentTool &&
            [CornerstoneTool.Pan, CornerstoneTool.Zoom].includes(currentTool))
        ) {
          this.cornerStoneService.activeMouseWheelTool();
          return;
        }
        this.cornerStoneService.additionalToolActivate(
          CornerstoneAdditionalTool.CustomZoomMouseWheel
        );
      });
  }

  private _subscriptionForMouseDrag(): void {
    fromEvent(document, 'mousedown')
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(() => {
        this.stateManagerProjectService.mouseWasDragged = true;
      });
    fromEvent(document, 'mouseup')
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((_) => {
        this.stateManagerProjectService.mouseWasDragged = false;
      });
  }

  private _subscriptionForDataProcessing(): void {
    combineLatest([
      this._isImageProcessed$,
      this._originalMaskBlob$,
      this._updatedMaskBlob$,
    ])
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(
        async ([isImageProcessed, originalMaskBlob, updatedMaskBlob]) => {
          if (
            !isImageProcessed ||
            !originalMaskBlob ||
            updatedMaskBlob === undefined
          ) {
            return;
          }

          if (!updatedMaskBlob) {
            await this.cornerStoneService
              .processNrrdMask(originalMaskBlob, true, false)
              .catch((_) => {
                if (this.stateManagerProjectService.isViewerLoaded) {
                  this._catchForProcessing();
                }
              });
          } else {
            await this.cornerStoneService
              .processNrrdMask(originalMaskBlob, true)
              .catch((_) => {
                if (this.stateManagerProjectService.isViewerLoaded) {
                  this._catchForProcessing()
                }
              });

            await this.cornerStoneService
              .processNrrdMask(updatedMaskBlob, false)
              .catch((_) => {
                if (this.stateManagerProjectService.isViewerLoaded) {
                  this._catchForProcessing()
                }
              });
          }
        }
      );
  }

  private _setClaimantUser(claimantUser: IClaimantDetailsDto) {
    this.stateManagerProjectService.claimantUser = claimantUser;
  }

  private _fireUpdateOfToken() {
    if (this._subscriptionToRefreshToken !== undefined) {
      return;
    }
    this._subscriptionToRefreshToken = from(
      this._authService.refreshToken()
    ).subscribe(
      (event) => {
        this._subscriptionToRefreshToken?.unsubscribe();
        this._subscriptionToRefreshToken = undefined;
      },
      (error: HttpErrorResponse) => {
        if (error instanceof HttpErrorResponse) {
          const responseMessage = error.error ? error.error.message : '';
          const responseErrorCode = error.error ? error.error.errorCode : '';
          if (error.status === 400) {
            if (
              responseErrorCode === '1059' &&
              !this._route.url.includes('/signin')
            ) {
              this._authService.signOut();
            }
          } else if (error.status === 401) {
            const tokenMessage = error.error.message;
            if (
              (!!responseMessage &&
                responseMessage.toLowerCase().includes('unauthorized')) ||
              (!!tokenMessage &&
                tokenMessage.includes('The incoming token has expired'))
            ) {
              this._authService.signOut();
            }
          }
          this._subscriptionToRefreshToken?.unsubscribe();
          this._subscriptionToRefreshToken = undefined;
        }
      }
    );
  }

  private _findViewWindow(index: ViewWindowIndex): HTMLElement {
    if (index === 1) index = 0;
    else if (index === 4) index = 1;
    return this.viewsRefs.find((_, i) => i === index)!.nativeElement;
  }

  handlePanToolByMmb(): void {
    MMB.panToolSelectionToggler(this.hostElement, this.stateManagerProjectService);
    MMB.panToolSub$.pipe(takeUntil(MMB.destroyObs$)).subscribe((selectionState) => this.isPanToolSelectedByMmb = selectionState);
    MMB.pan3dToolSub$.pipe(takeUntil(MMB.destroyObs$)).subscribe((selectionState) => this.is3dPanToolSelectedByMmb = selectionState);
  }
}
